'use client';

import { ComponentProps, createContext, useContext } from 'react';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';
import { Layout } from '@/shared/ui/layout';
import { Media } from '@/shared/ui/media';
import { Media as MediaType } from '@/shared/ui/media/types';
import { ContactNotSeenAvatar } from './contact-not-seen-avatar';
import './contact.scss';
import { SocialMediaPlatform } from './types';

const getSocialMediaIconName = (platform: SocialMediaPlatform) => {
    switch (platform) {
        case SocialMediaPlatform.FACEBOOK:
            return 'social-facebook' as const;
        case SocialMediaPlatform.INSTAGRAM:
            return 'social-instagram' as const;
        case SocialMediaPlatform.TIKTOK:
            return 'social-tiktok' as const;
    }
};

type ContactSocialMediaBoxContextValue = {
    data?: MediaType;
    platform?: SocialMediaPlatform;
    isNotSeen?: boolean;
};

const ContactSocialMediaBoxContext = createContext<ContactSocialMediaBoxContextValue>(
    {} as ContactSocialMediaBoxContextValue,
);

type ContactSocialMediaBoxWrapperProps = ComponentProps<typeof Layout> & {
    data: MediaType;
    platform: SocialMediaPlatform;
    isNotSeen?: boolean;
};

export const ContactSocialMediaBoxWrapper = ({
    data,
    platform,
    isNotSeen = false,
    children,
    ...props
}: ContactSocialMediaBoxWrapperProps) => {
    const contextValue: ContactSocialMediaBoxContextValue = {
        data,
        platform,
        isNotSeen,
    };

    return (
        <ContactSocialMediaBoxContext.Provider value={contextValue}>
            <Layout className={cn('card', 'contact__box', 'contact__social-media-box')} {...props}>
                {children}
            </Layout>
        </ContactSocialMediaBoxContext.Provider>
    );
};

type ContactSocialMediaBoxMediaProps = ComponentProps<typeof Layout> & {
    data?: MediaType;
};

export const ContactSocialMediaBoxMedia = ({ data, ...props }: ContactSocialMediaBoxMediaProps) => {
    const context = useContext(ContactSocialMediaBoxContext);
    const mediaData = data ?? context.data;

    if (!mediaData) {
        return null;
    }

    return (
        <Layout {...props}>
            <Media data={mediaData} />
        </Layout>
    );
};

type ContactSocialMediaBoxNotSeenAvatarProps = ComponentProps<typeof ContactNotSeenAvatar>;

export const ContactSocialMediaBoxNotSeenAvatar = ({ ...props }: ContactSocialMediaBoxNotSeenAvatarProps) => {
    const context = useContext(ContactSocialMediaBoxContext);
    if (!context.isNotSeen) {
        return null;
    }
    return <ContactNotSeenAvatar {...props} />;
};

type ContactSocialMediaBoxPlatformProps = ComponentProps<typeof Layout>;

export const ContactSocialMediaBoxPlatform = ({ media, ...props }: ContactSocialMediaBoxPlatformProps) => {
    const context = useContext(ContactSocialMediaBoxContext);
    const platform = context.platform;

    if (!platform) {
        return null;
    }

    const iconName = getSocialMediaIconName(platform);

    return (
        <Layout className="contact__social-media-box__platform" media={media} {...props}>
            <Icon name={iconName} />
        </Layout>
    );
};

// Legacy export for backward compatibility
export const ContactSocialMediaBox = ContactSocialMediaBoxWrapper;

// Compound component structure
export const ContactSocialMediaBoxCompound: typeof ContactSocialMediaBoxWrapper & {
    Media: typeof ContactSocialMediaBoxMedia;
    NotSeenAvatar: typeof ContactSocialMediaBoxNotSeenAvatar;
    Platform: typeof ContactSocialMediaBoxPlatform;
} = Object.assign(ContactSocialMediaBoxWrapper, {
    Media: ContactSocialMediaBoxMedia,
    NotSeenAvatar: ContactSocialMediaBoxNotSeenAvatar,
    Platform: ContactSocialMediaBoxPlatform,
});
